import React from 'react';
import { useResponsive, Spinner } from '@parsina/framework';

import { C } from '../../constants/theme';
import { useTranslation } from '../../i18n/I18nProvider.jsx';
import { useMe } from '../../hooks/useMe.js';
import { errorCode, errorField, errorMessageKey } from '../../lib/bffClient.js';
import { readJson, writeJson, clearDraft, ONBOARDING_DRAFT_KEY } from '../../lib/drafts.js';
import OnboardingWeb from './Onboarding.web.jsx';
import OnboardingMobile from './Onboarding.mobile.jsx';
import {
  emptyOnboardingForm, validateOnboarding, toOnboardingInput, fieldError,
} from './Onboarding.shared.js';

// «به پارسینا خوش آمدید» — the one step between a Keycloak account and an app.
//
// core.md §4.3. Rendered by the BOOT, not by a route guard: App.jsx asks
// `onboardingStatus` before it mounts the router and renders this instead when
// the answer is false. That is the doctor app's pattern and it exists because a
// route guard is a redirect, and a redirect is a thing a half-typed form does
// not survive.
//
// ── The four stages, and the one that must never be reached by accident ───
//
//   gate    the status query is in flight — a spinner, nothing else
//   form    the seven fields
//   saving  the mutation is out; the values stay on screen and stay typed
//   done    the store now holds a profile, and App.jsx swaps to the router
//
// A FAILED status query is not one of them, and it is not rendered HERE: the
// boot gate in App.jsx owns that state and shows a full-page ErrorState with a
// retry («سرویس در دسترس نیست»). This component is mounted only once the status
// has answered `isOnboarded: false` — a form is shown because the status said
// so, never because a query failed. If the store's error is ever set while
// this is mounted anyway, nothing is rendered rather than the form: a form that
// saves into a store which is not answering loses everything typed into it.
//
// ── The draft survives a re-login ─────────────────────────────────────────
//
// The session dialog (§15) overlays this screen without unmounting it, but its
// «ورود مجدد» is a full-document navigation to Keycloak and back, and a
// `useState` form does not come back from one. So the values are mirrored into
// `sessionStorage.onboarding_draft` as they are typed, restored on mount, and
// cleared the moment the submission succeeds. sessionStorage, not localStorage:
// this is THIS tab's typing, and it must not be presented to a different
// student on the same machine tomorrow.
//
// ── Already onboarded ─────────────────────────────────────────────────────
//
// There is no redirect here, because there is no router here. A student who
// types `/onboarding` while onboarded never reaches this component: App.jsx
// mounts the router for them, and the router's catch-all lands them on
// `/patients` — which is the redirect core.md §4.3 asks for, arrived at by not
// rendering a screen rather than by leaving one.

/** The stored draft, folded over an empty form so a missing key cannot render `undefined`. */
const restoreDraft = () => {
  const empty = emptyOnboardingForm();
  const saved = readJson(ONBOARDING_DRAFT_KEY, { kind: 'session', fallback: null });
  if (!saved || typeof saved !== 'object') return empty;
  const form = { ...empty };
  for (const key of Object.keys(empty)) {
    if (typeof saved[key] === 'string') form[key] = saved[key];
  }
  return form;
};

const OnboardingScreen = ({ onDone }) => {
  const { isMobile } = useResponsive();
  const { t } = useTranslation();
  const { status, loading, error, completeOnboarding } = useMe();

  const [values, setValues] = React.useState(restoreDraft);
  const [errors, setErrors] = React.useState({});
  const [submitError, setSubmitError] = React.useState('');
  const [saving, setSaving] = React.useState(false);

  // Mirror the typing into the draft. Written on every change rather than on
  // a timer: the event this guards against (the session ending) is not one the
  // student schedules, and a 2s debounce would lose the last two seconds of
  // exactly the field they were on.
  React.useEffect(() => {
    writeJson(ONBOARDING_DRAFT_KEY, values, { kind: 'session' });
  }, [values]);

  // Pre-fill the name from the token ONCE, when the status lands — and only
  // into a field the student has not touched (a restored draft counts as
  // touched). Re-applying it on every publish would overwrite a correction the
  // moment the balance refreshed.
  const prefilled = React.useRef(false);
  React.useEffect(() => {
    if (prefilled.current || !status?.suggestedFullName) return;
    prefilled.current = true;
    setValues((prev) => (prev.fullName ? prev : { ...prev, fullName: status.suggestedFullName }));
  }, [status?.suggestedFullName]);

  const onChange = React.useCallback((patch) => {
    setValues((prev) => ({ ...prev, ...patch }));
    // Clear only the fields being edited: an error the student has not looked
    // at yet must stay on screen, and one they are fixing must not shout while
    // they are mid-word.
    setErrors((prev) => {
      const next = { ...prev };
      for (const key of Object.keys(patch)) delete next[key];
      return next;
    });
  }, []);

  const onSubmit = React.useCallback(async () => {
    const problems = validateOnboarding(values);
    setErrors(problems);
    if (Object.keys(problems).length) return;

    setSaving(true);
    setSubmitError('');
    try {
      await completeOnboarding(toOnboardingInput(values));
      // The profile is stored: the draft has nothing left to protect, and a
      // stale one would be restored over the NEXT account's empty form.
      clearDraft(ONBOARDING_DRAFT_KEY, { kind: 'session' });
      onDone?.();
    } catch (err) {
      // INVALID_INPUT names the field it rejected — and, when the BFF said WHY,
      // that sentence goes under the field instead of the catalogue's generic
      // «معتبر نیست». Anything else is a sentence above the form. Either way
      // the typed values stay exactly where they are — this is the moment a
      // student is most likely to give up, and retyping seven fields is how
      // they do it.
      const field = errorField(err);
      if (errorCode(err) === 'INVALID_INPUT' && field) {
        setErrors((prev) => ({ ...prev, [field]: fieldError(err) }));
        setSubmitError(t('errors.invalidInput'));
      } else {
        setSubmitError(t(errorMessageKey(err)) || t('onboarding.error.unavailable'));
      }
    } finally {
      setSaving(false);
    }
  }, [values, completeOnboarding, onDone, t]);

  // The status query failed. The form is not an option — and the error screen
  // is App.jsx's, not this component's (see the header).
  if (error && !status) return null;

  if (loading && !status) {
    return (
      <div style={GATE} data-testid="onboarding-gate">
        <Spinner />
      </div>
    );
  }

  const Surface = isMobile ? OnboardingMobile : OnboardingWeb;
  return (
    <Surface
      values={values}
      onChange={onChange}
      errors={errors}
      submitError={submitError}
      saving={saving}
      onSubmit={onSubmit}
      phone={status?.phone || ''}
    />
  );
};

const GATE = {
  position: 'fixed',
  inset: 0,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  background: C.bg,
  padding: 24,
};

export default OnboardingScreen;
